import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Checkbox,
  FormGroup,
  MenuItem,
  Select,
  InputLabel,
  Alert,
  Card,
  CardContent,
  CardHeader
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import axios from 'axios';

const QuestionnairePreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // In a real application, we would fetch this data from the backend
    // For now, we'll use mock data
    const mockQuestionnaires = [
      {
        id: 1,
        title: 'Job Application Form',
        description: 'Form for job applicants to submit their details',
        categories: [1, 2, 3, 4],
        questions: [1, 2, 3, 4, 5, 6, 7, 8],
        active: true
      },
      {
        id: 2,
        title: 'Internship Application',
        description: 'Form for internship applicants',
        categories: [1, 2, 4],
        questions: [1, 2, 4, 5, 8],
        active: true
      }
    ];
    
    const mockQuestions = [
      {
        id: 1,
        title: 'Full Name',
        description: 'Enter your full legal name',
        type: 'short_text',
        required: true,
        category_id: 1,
        options: [],
        has_follow_up: false,
        follow_up_condition: '',
        follow_up_value: '',
        review_guidelines: 'Check if name matches official records'
      },
      {
        id: 2,
        title: 'Email Address',
        description: 'Enter your email address',
        type: 'short_text',
        required: true,
        category_id: 1,
        options: [],
        has_follow_up: false,
        follow_up_condition: '',
        follow_up_value: '',
        review_guidelines: 'Verify email format is valid'
      },
      {
        id: 3,
        title: 'Phone Number',
        description: 'Enter your contact number',
        type: 'short_text',
        required: true,
        category_id: 1,
        options: [],
        has_follow_up: false,
        follow_up_condition: '',
        follow_up_value: '',
        review_guidelines: 'Check phone number format'
      },
      {
        id: 4,
        title: 'Highest Degree',
        description: 'What is your highest level of education?',
        type: 'radio',
        required: true,
        category_id: 2,
        options: ['High School', 'Associate Degree', 'Bachelor\'s Degree', 'Master\'s Degree', 'PhD'],
        has_follow_up: true,
        follow_up_condition: 'equals',
        follow_up_value: 'Bachelor\'s Degree',
        follow_up_question_id: 5,
        review_guidelines: 'Verify education claims with certificates if available'
      },
      {
        id: 5,
        title: 'University/Institution',
        description: 'Where did you complete your degree?',
        type: 'short_text',
        required: true,
        category_id: 2,
        options: [],
        has_follow_up: false,
        follow_up_condition: '',
        follow_up_value: '',
        review_guidelines: 'Check if institution is accredited'
      },
      {
        id: 6,
        title: 'Years of Experience',
        description: 'How many years of relevant work experience do you have?',
        type: 'dropdown',
        required: true,
        category_id: 3,
        options: ['Less than 1 year', '1-3 years', '3-5 years', '5-10 years', 'More than 10 years'],
        has_follow_up: false,
        follow_up_condition: '',
        follow_up_value: '',
        review_guidelines: 'Verify with resume timeline'
      },
      {
        id: 7,
        title: 'Current Position',
        description: 'What is your current job title?',
        type: 'short_text',
        required: true,
        category_id: 3,
        options: [],
        has_follow_up: false,
        follow_up_condition: '',
        follow_up_value: '',
        review_guidelines: 'Compare with LinkedIn profile if available'
      },
      {
        id: 8,
        title: 'Programming Languages',
        description: 'Which programming languages are you proficient in?',
        type: 'checkbox',
        required: true,
        category_id: 4,
        options: ['JavaScript', 'Python', 'Java', 'C#', 'PHP', 'Ruby', 'Go', 'Swift'],
        has_follow_up: false,
        follow_up_condition: '',
        follow_up_value: '',
        review_guidelines: 'Ask for code samples during interview'
      }
    ];
    
    // Find the questionnaire by ID
    const questionnaire = mockQuestionnaires.find(q => q.id === parseInt(id));
    
    if (questionnaire) {
      // Get the questions for this questionnaire
      const questionnaireQuestions = mockQuestions.filter(q => questionnaire.questions.includes(q.id));
      
      setQuestionnaire(questionnaire);
      setQuestions(questionnaireQuestions);
      setLoading(false);
    } else {
      setError('Questionnaire not found');
      setLoading(false);
    }
    
    // In a real application, we would fetch data like this:
    /*
    axios.get(`/api/admin/questionnaires/${id}/preview`)
      .then(response => {
        setQuestionnaire(response.data.questionnaire);
        setQuestions(response.data.questions);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching questionnaire preview:', error);
        setError('Failed to load questionnaire');
        setLoading(false);
      });
    */
  }, [id]);

  const handleBackToList = () => {
    navigate('/admin/questionnaires');
  };

  const handleEditQuestionnaire = () => {
    navigate('/admin/questionnaires', { state: { editId: id } });
  };

  const renderQuestionPreview = (question) => {
    switch (question.type) {
      case 'short_text':
        return (
          <TextField
            fullWidth
            label="Your answer"
            variant="outlined"
            size="small"
            disabled
          />
        );
      
      case 'long_text':
        return (
          <TextField
            fullWidth
            label="Your answer"
            variant="outlined"
            multiline
            rows={4}
            disabled
          />
        );
      
      case 'radio':
        return (
          <FormControl component="fieldset">
            <RadioGroup>
              {question.options.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option}
                  control={<Radio disabled />}
                  label={option}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );
      
      case 'checkbox':
        return (
          <FormGroup>
            {question.options.map((option, index) => (
              <FormControlLabel
                key={index}
                control={<Checkbox disabled />}
                label={option}
              />
            ))}
          </FormGroup>
        );
      
      case 'dropdown':
        return (
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>Select an option</InputLabel>
            <Select
              label="Select an option"
              disabled
            >
              {question.options.map((option, index) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      
      case 'file':
        return (
          <Button
            variant="outlined"
            component="label"
            disabled
          >
            Upload File
          </Button>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading preview...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToList}
          sx={{ mt: 2 }}
        >
          Back to Questionnaires
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToList}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Preview: {questionnaire.title}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={handleEditQuestionnaire}
        >
          Edit Questionnaire
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          {questionnaire.title}
        </Typography>
        <Typography variant="body1" paragraph>
          {questionnaire.description}
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          This is a preview of how the questionnaire will appear to respondents.
        </Alert>
      </Paper>

      <Box sx={{ mb: 4 }}>
        {questions.map((question, index) => (
          <Card key={question.id} sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" component="div">
                  {index + 1}. {question.title}
                  {question.required && (
                    <Box component="span" sx={{ color: 'error.main', ml: 1 }}>
                      *
                    </Box>
                  )}
                </Typography>
                {question.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {question.description}
                  </Typography>
                )}
              </Box>
              
              <Box sx={{ mt: 3 }}>
                {renderQuestionPreview(question)}
              </Box>
              
              {question.has_follow_up && (
                <Box sx={{ mt: 3, pl: 3, borderLeft: '2px solid', borderColor: 'primary.light' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Follow-up question (shown conditionally):
                  </Typography>
                  
                  {questions.find(q => q.id === question.follow_up_question_id) && (
                    <Box>
                      <Typography variant="subtitle2">
                        {questions.find(q => q.id === question.follow_up_question_id).title}
                        {questions.find(q => q.id === question.follow_up_question_id).required && (
                          <Box component="span" sx={{ color: 'error.main', ml: 1 }}>
                            *
                          </Box>
                        )}
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        {renderQuestionPreview(questions.find(q => q.id === question.follow_up_question_id))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToList}
        >
          Back to Questionnaires
        </Button>
        <Button
          variant="contained"
          disabled
        >
          Submit (Preview Only)
        </Button>
      </Box>
    </Box>
  );
};

export default QuestionnairePreview;
